import {ANIMATION_DURATION, GridLayout, GridLayoutFactory} from "../../common/gridLayout";
import {ScrollService} from "../../common/scroll";
import {CONFIRMABLE_CHANNELS, ICONS} from "./feedCommons";
import {resolve} from "../../container";
import {Timeout} from "../../common/timeout";
import {LocatedDates} from "../../common/utils/dates/locatedDates";
import {TemplateModel} from "../../common/template";
import {schedule} from "../../common/utils/promises";
import {html, LitElement, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query, state} from "lit/decorators.js";
import StylesTile from "./socialWallTile.lit.scss";
import Styles from "./socialWall.lit.scss";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import type {DirectiveResult} from "lit/directive.js";
import {Initializing, ShadowHighlightContextAware} from "../../common/elements";
import {DefaultFeedSource} from "./feedSource";
import {FeedEntry, TileVariant} from "./feedEntry";
import type {EopOverlaySpinner} from "../../page/elements/spinner";
import {ManagingResources} from "../../common/lifetime";
import {IntersectionObserverFactory, onceIntersected} from "../../common/observation";
import type {Maybe, Nullable} from "../../bootstrap/common/types";
import {trackingButtonInteract, trackingTeaserInteract} from "../../tracking/trackingAttributes";
import {Dictionary} from "../../page/elements/dictionary";


@customElement("eop-social-wall")
export class EopSocialWall extends Initializing(ManagingResources(LitElement)) {

    public static readonly styles = Styles;

    @state()
    private feedEntries: FeedEntry[] = [];
    @property({attribute: "number", type: Number})
    private limit: number = 3;
    @property({attribute: "fetch-more-amount", type: Number})
    private fetchMoreAmount: number = 0;

    @query(".social-wall-tiles")
    private tilesContainer: HTMLElement;
    @query("eop-overlay-spinner")
    private overlaySpinner: EopOverlaySpinner;

    private gridLayout: GridLayout;
    private tilesContainerObserver: IntersectionObserver;
    private dictionary: Dictionary;

    public constructor(
        private feedSource: DefaultFeedSource = new DefaultFeedSource(),
        private timeout: Timeout = resolve(Timeout),
        private scrollService: ScrollService = resolve(ScrollService),
        private gridLayoutFactory: GridLayoutFactory = resolve(GridLayoutFactory),
        private intersectionObserverFactory: IntersectionObserverFactory = resolve(IntersectionObserverFactory)
    ) {
        super();
    }

    public render(): TemplateResult {
        return html`
            <eop-overlay-spinner></eop-overlay-spinner>
            <div class="social-wall-tiles">
                <div class="small-tile grid-element grid-reference-element"></div>
                ${this.renderTiles()}
            </div>
            ${this.renderFetchMoreButton()}
        `;
    }

    private renderTiles(): Nullable<TemplateResult[]> {
        if (this.feedEntries.isEmpty()) {
            return null;
        }
        return this.feedEntries.map(entry => html`
            <eop-social-wall-tile .entry=${entry}></eop-social-wall-tile>
        `);
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
        this.feedSource.configure({
            sources: this.getAttribute("sources")?.parseAsJSON() ?? {},
            keywords: this.getAttribute("keywords")?.parseCommaSeparated() ?? [],
            excludedKeywords: this.getAttribute("excluded-keywords")?.parseCommaSeparated() ?? [],
            characterLimit: this.getAttribute("character-limit")?.toInt() ?? 180,
            language: this.getAttribute("language") ?? ""
        });
        this.tilesContainerObserver = this.intersectionObserverFactory.create(onceIntersected(() => this.scheduleLoadingTiles()));
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        this.tilesContainerObserver.observe(this.tilesContainer);
    }

    private renderFetchMoreButton(): Nullable<TemplateResult> {
        const fetchMoreLabel = this.dictionary.translate("MSG_TEASER_FETCH_MORE");
        if (this.showFetchMore()) {
            return html`
                <div class="fetch-more-button">
                    <button data-target="button" type="button" class="primary" @click=${this.fetchMoreHandler} ${trackingButtonInteract(fetchMoreLabel)}>
                        ${fetchMoreLabel}
                    </button>
                </div>
            `;
        }
        return null;
    }

    private scheduleLoadingTiles(): void {
        void schedule(this.overlaySpinner.spinWhile(this.fetchInitial())).as("fetch");
    }

    private async fetchMoreHandler(): Promise<void> {
        const offset = this.feedEntries.length;
        await schedule(this.overlaySpinner.spinWhile(this.fetchMore(offset)))
            .as("fetch-more");
    }

    private async fetchInitial(): Promise<void> {
        await this.fetchTiles(this.limit, 0);
        this.initialized();
        this.gridLayout = this.gridLayoutFactory.create(this.tilesContainer, ".grid-element", ".grid-reference-element");
        this.gridLayout.initLayout();
    }

    private async fetchMore(offset: number): Promise<void> {
        await this.fetchTiles(this.fetchMoreAmount, offset);
        this.scrollToNextElement(offset);
    }

    private scrollToNextElement(offset: number): void {
        void this.timeout.delay(() => {
            const nextTile = this.renderRoot.querySelectorAll<EopSocialWall>(".social-wall-tile").item(offset);
            void this.scrollService.scrollToElement(this.tilesContainer, -Number.parseInt(nextTile.style.top), ANIMATION_DURATION);
        });
    }

    private showFetchMore(): boolean {
        return this.fetchMoreAmount > 0
            && this.feedSource.total() > this.feedEntries.length;
    }

    private async fetchTiles(size: number, offset: number): Promise<FeedEntry[]> {
        const entries = await this.feedSource.fetch(size, offset);
        const newlyAddedTiles = entries.map(entry => {
            return FeedEntry.fromResponse(entry)
                .inChannelContext(this.feedSource.channels());
        });
        this.feedEntries = this.feedEntries.slice(0, offset).concat(newlyAddedTiles);
        return newlyAddedTiles;
    }
}

@customElement("eop-social-wall-tile")
export class EopSocialWallTile extends ShadowHighlightContextAware(LitElement) {

    public static readonly styles = StylesTile;

    @property()
    public entry: FeedEntry;

    public constructor(
        private locatedDates: LocatedDates = resolve(LocatedDates)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.classList.add("social-wall-tile", "grid-element", this.entry.getTileStyle());

    }

    public render(): TemplateResult {
        return this.needsConfirmation()
            ? this.renderConfirmableTile()
            : this.renderTile();
    }

    private needsConfirmation(): boolean {
        return !!this.entry.data.contentType && CONFIRMABLE_CHANNELS.includes(this.entry.data.contentType);
    }

    private renderConfirmableTile(): TemplateResult {
        return html`
            <eop-load-after-confirmation
                    module-name="youtube"
                    confirmation-text-key="YOUTUBE_CONFIRMATION_TEXT"
                    button-label-key="YOUTUBE_CONFIRMATION_BUTTON_LABEL"
            >
                <span slot="content-to-confirm">
                    ${this.renderTile()}
                </span>
            </eop-load-after-confirmation>
        `;
    }

    private renderTile(): TemplateResult {
        const trackingLabel = this.entry.data.headline || `social-wall-tile-${this.entry.data.contentType}`;
        return html`
            <a href=${this.entry.data.url} target="_blank"
               class="social-wall-entry reverse-background"
               ${trackingTeaserInteract(trackingLabel, this.entry.data.url)}
            >
                <div class="tile-media ${this.entry.getTileStyle()}">${this.imageElement()}</div>
                <div class="tile-text">
                    ${this.iconElement()}
                    <div class="metadata">${this.metaData()}</div>
                    <div class="tile-headline">${this.headlineText()}</div>
                    <div class="description-text">${this.descriptionText()}</div>
                </div>
            </a>`;
    }

    private headlineText(): DirectiveResult | string {
        return this.entry.data.headline ? unsafeHTML(this.entry.data.headline) : "";
    }

    private descriptionText(): DirectiveResult | string {
        return this.entry.data.subHeadline ? unsafeHTML(this.entry.data.subHeadline) : "";
    }

    private imageElement(): TemplateResult | Text {
        if (this.entry.data.contentType === "youtube") {
            return html`
                <eop-confirmable-iframe-element
                        src=${this.entry.data.url}
                        module-name="youtube"
                        frame-title=${this.entry.data.headline}
                        .additionalClasses=${this.classesToCenterYoutube()}
                ></eop-confirmable-iframe-element>`;
        }
        const forceImage = this.entry.getTileStyle() === TileVariant.DOUBLE;
        const imageUrl = this.entry.getImageUrl(forceImage);
        if (!imageUrl) {
            return document.createTextNode("");
        }
        switch (this.entry.data.mediaType) {
            case "responsiveImage": {
                return html`
                    <eop-responsive-image
                            image-src=${imageUrl}
                            image-alt=${this.entry.data.imageAltText ?? ""}
                            intrinsic-aspect-ratio="true"
                    ></eop-responsive-image>`;
            }
            case "image": {
                return html`
                    <eop-plain-image
                            src=${imageUrl}
                            alt=${this.entry.data.imageAltText ?? ""}
                            class="plain-image-container eop-image"
                    ></eop-plain-image>`;
            }
            case "video":
            case "none":
            default: {
                return document.createTextNode("");
            }
        }
    }


    private classesToCenterYoutube(): Record<string, boolean> {
        return {
            "youtube-centered-vertically": true,
            "double-tile": this.entry.getTileStyle() === "double-tile"
        };
    }

    private iconElement(): Maybe<Element | Text> {
        const icon = this.entry.data.contentType ? ICONS[this.entry.data.contentType] : null;
        if (!icon) {
            return document.createTextNode("");
        }
        return new TemplateModel(icon)
            .addClasses("tile-icon")
            .asElement();
    }

    private metaData(): string {
        const date = this.locatedDates.toLocalDateString(new Date(this.entry.data.modifiedDate));
        const contentTypeDescription = this.entry.pageTypeDescription();
        if (contentTypeDescription) {
            return `${date} | ${contentTypeDescription}`;
        }
        return `${date}`;
    }
}